import {
  InvoiceRow,
  WorkLog,
  WorkLogConvertedResourceType
} from "@wingspanhq/wingspan-api-sdk";
import uniq from "lodash/uniq";
import { useMemo } from "react";
import { queriesInvoiceRows } from "@query";

/**
 * Indexes the invoices a page of work logs converted into, by invoice id.
 *
 * v1 fetched each invoice one by one (`GET /payments/invoice/:id` per id); v3
 * batches them through the invoice search rows (`filter[id][anyOf]`), which
 * carry the `status` and `invoiceNumber` the work-log status cells read. Only
 * work logs that converted into an *invoice* are looked up — a W-2 work log
 * converts into a pay statement, whose id belongs to a different collection.
 */
export const useInvoicesMap = (workLogs: WorkLog[] | undefined) => {
  const invoiceIds = useMemo(
    () =>
      uniq(
        (workLogs || [])
          .filter(
            workLog =>
              workLog.convertedResourceType ===
              WorkLogConvertedResourceType.INVOICE
          )
          .map(workLog => workLog.convertedResourceId)
          .filter((id): id is string => !!id)
      ),
    [workLogs]
  );

  const queryInvoiceRows = queriesInvoiceRows.searchInvoiceRows.useQueryAll(
    { filter: { id: { anyOf: invoiceIds } } },
    { enabled: invoiceIds.length > 0 }
  );

  const invoicesMap = useMemo(() => {
    const map = new Map<string, InvoiceRow>();
    queryInvoiceRows.flatData.forEach(invoiceRow => {
      if (invoiceRow.id) {
        map.set(invoiceRow.id, invoiceRow);
      }
    });
    return map;
  }, [queryInvoiceRows.flatData]);

  return {
    invoicesMap,
    isLoading: queryInvoiceRows.isLoading && invoiceIds.length > 0
  };
};
